import type { ReactNode } from 'react';
import { Icon } from '../../icons/Icon';
import styles from './ErrorPanel.module.css';

export interface ErrorPanelProps {
    /**
     * What failed, and where possible what to do about it — sentence case, no trailing period (§9).
     * `coding-standards.md §3.2` asks an error to name the failure rather than describe the
     * exception, so this is prose for a rep, not a message from a stack trace.
     */
    readonly message: string;
    /**
     * The retry affordance, required. §5's error state is "tone/danger inline panel **with
     * retry**" — an error a user can only read is a dead end, and an optional slot is how the
     * retry stops being rendered.
     */
    readonly action: ReactNode;
    readonly 'data-testid'?: string;
}

/**
 * §5's inline error state — a `tone/danger` panel carrying a message and a retry.
 *
 * `role="alert"` so the failure is announced when it appears rather than only when someone
 * navigates onto it. This is the inline, in-place failure; the whole-screen one is the app shell's
 * FailureSurface, which additionally carries the "your data is safe" line because an emptied screen
 * gets read as data loss. An inline panel does not empty the screen, so it does not need that line.
 */
export function ErrorPanel({ message, action, 'data-testid': testId }: ErrorPanelProps) {
    return (
        <div className={styles.panel} data-testid={testId} role="alert">
            <span className={styles.glyph}>
                <Icon emphasis="regular" name="error" size="md" />
            </span>
            <p className={styles.message}>{message}</p>
            {action}
        </div>
    );
}
